<script setup lang="ts">
import { computed } from "vue";

import ArticleContent from "@/components/article/ArticleContent.vue";
import ArticleContentCollapser from "@/components/article/ArticleContentCollapser.vue";
import type { GreenlandicArticle, GreenlandicLemma } from "@/types/article-formats/greenlandic";

import GreenlandicArticleContentLemma from "./GreenlandicArticleContentLemma.vue";
import GreenlandicArticleContentTranslation from "./GreenlandicArticleContentTranslation.vue";

const props = defineProps<{
  article: GreenlandicArticle;
  isCollapsible: boolean;
}>();

const sortedTranslations = computed<GreenlandicLemma[]>(() => {
  const translations = [...props.article.translations];
  translations.sort((a, b) => a.language.localeCompare(b.language));
  return translations;
});
</script>

<template>
  <ArticleContent>
    <div class="article-content__header">
      <GreenlandicArticleContentLemma
        v-for="(lemma, index) in article.headerLemmas"
        :key="index"
        class="article-content__lemma"
        :lemma
        :is-last="index === article.headerLemmas.length - 1"
      />
    </div>

    <ArticleContentCollapser :is-collapsible="props.isCollapsible">
      <div class="article-content__body-container article-content__body-container--reading-lines">
        <div class="article-content__text indentation-container">
          <div class="article-content__translations">
            <GreenlandicArticleContentTranslation
              v-for="(translation, index) in sortedTranslations"
              :key="index"
              :translation
            />
          </div>

          <div class="article-content__explanations">
            <div
              v-for="(explanation, index) in props.article.explanations"
              :key="index"
              class="article-content__explanation"
            >
              <span v-if="index === 0" class="article-content__explanation-title">
                Forklaringer:
              </span>
              <span class="article-content__explanation-text">
                {{ explanation.explanation }}
              </span>
            </div>
          </div>

          <div class="article-content__notes">
            <div
              v-for="(note, index) in props.article.notes"
              :key="index"
              class="article-content__note"
            >
              <span v-if="index === 0" class="article-content__note-title">Noter:</span>
              <span class="article-content__note-text">
                {{ note.note }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </ArticleContentCollapser>
  </ArticleContent>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.article-content {
  &__header {
    padding: 12px 14px 12px 20px;
  }

  &__lemma {
    display: inline-flex;
    margin-right: 20px;
  }

  &__explanations,
  &__notes {
    margin-left: 8px;
  }

  &__explanation,
  &__note {
    display: grid;
    grid-template-columns: 180px 1fr;
    column-gap: 12px;
    row-gap: 50px;

    &-title {
      grid-column: 1;
      color: rgb(@theme-colors[@grey][110]);
    }

    &-text {
      grid-column: 2;
    }
  }

  &__explanation-text {
    color: rgb(@theme-colors[@primary][base]);
  }

  &__note-text {
    color: rgb(@theme-colors[@secondary][50]);
  }
}
</style>
